import React, {FC} from 'react';
import styled from '@emotion/styled';
import {twMerge} from 'tailwind-merge';
import {Spinner} from './Spinner';

interface ButtonProps {
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'xsmall' | 'small' | 'normal' | 'medium' | 'large';
  icon?: any;
  label: string;
  style?: any;
  spinner?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}

const ButtonWrapper = styled.button`
  height: 32px;
  color: white;
  cursor: pointer;
  transition: 0.15s;
  font-size: 13px;
  font-weight: 500;
  position: relative;
  border-radius: 6px;
  align-items: center;
  display: inline-flex;
  justify-content: center;
  white-space: nowrap;
  background-color: #2fa062;
  border: 1px solid transparent;

  &.primary {
    color: #fff;
    border-color: #2fa062;
    background-color: #2fa062;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.06);

    &:hover {
      border-color: #278a54;
      background-color: #278a54;
    }
  }

  &.secondary {
    color: #654dcb;
    border-color: #efedfb;
    background-color: #efedfb;

    &:hover {
      border-color: #e4e0f8;
      background-color: #e4e0f8;
    }
  }

  &.outline {
    color: #fff;
    border-color: #fff;
    background-color: transparent;

    &:hover {
      color: #654dcb;
      background-color: #fff;
      border-color: #fff;
    }
  }

  &.disabled {
    color: #9b9ea8;
    cursor: default;
    box-shadow: none;
    border-color: #e6e7eb;
    background-color: #e6e7eb;

    &:hover {
      color: #9b9ea8;
      border-color: #e6e7eb;
      background-color: #e6e7eb;
    }
  }
`;

const AnnotationWrapper = styled.div`
  top: 50%;
  display: flex;
  left: 10px;
  margin-top: -13px; // Assumes an approx. 17px-high icon
  position: absolute;
`;

// Shorter than the equivalent CSS
const heights = {xsmall: 24, small: 32, normal: 40, medium: 46, large: 52};
const hPaddings = {xsmall: 10, small: 14, normal: 20, medium: 26, large: 32};
const fontSizes = {xsmall: '12px', small: '13px', normal: '14px', medium: '15px', large: '16px'};
const borderRadii = {xsmall: 5, small: 6, normal: 6, medium: 7, large: 8};

export const Button: FC<ButtonProps> = ({
  variant = 'primary',
  size = 'small',
  type,
  label,
  icon,
  disabled = false,
  spinner = false,
  style,
  onClick,
  className = '',
}) => {
  const height = heights[size];
  const paddingLeft = !!icon ? hPaddings[size] - 4 : hPaddings[size];
  const paddingRight = hPaddings[size];
  const borderRadius = borderRadii[size];
  const fontSize = fontSizes[size];
  const customStyle = {height, fontSize, borderRadius, paddingLeft, paddingRight, ...(style || {})};

  return (
    <ButtonWrapper
      onClick={disabled ? () => {} : onClick}
      className={twMerge(`${size} ${variant} ${disabled ? 'disabled' : ''}`, className)}
      style={customStyle}
      type={type}>
      {spinner ? (
        <AnnotationWrapper>
          <Spinner size={20} mode={'light'} />
        </AnnotationWrapper>
      ) : (
        <></>
      )}
      {icon ? <span style={{marginRight: 8, display: 'inline-flex'}}>{icon}</span> : <></>}
      {label}
    </ButtonWrapper>
  );
};

interface IconButtonProps {
  icon: any;
  style?: any;
  disabled?: boolean;
  onClick: () => void;
}

const IconWrapper = styled.button`
  width: 56px;
  color: white;
  border: none;
  height: 56px;
  transition: 0.2s;
  padding: 10px 20px;
  align-items: center;
  border-radius: 56px;
  display: inline-flex;
  justify-content: center;
  background-color: #55b591;

  &:hover {
    cursor: pointer;
    background-color: #2b995b;
  }

  &.disabled {
    cursor: default;
    border-color: #aeb4bf;
    background-color: #aeb4bf;
  }
`;

export const IconButton: FC<IconButtonProps> = ({icon, style, disabled, onClick}) => {
  return (
    <IconWrapper onClick={disabled ? () => {} : onClick} className={disabled ? 'disabled' : ''} style={style}>
      <span dangerouslySetInnerHTML={icon} />
    </IconWrapper>
  );
};
